/** @jsx jsx */
import type { SerializedStyles } from '@emotion/react';
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { sortBy } from 'lodash';

import { FormattedMessage } from 'js/lib/coursera.react-intl';
import redirect from 'js/lib/coursera.redirect';

import { Button, RadioInput, Search, Table, Typography, Typography2, useTheme } from '@coursera/cds-core';
import type { ButtonProps, TableColumn, Theme } from '@coursera/cds-core';
import { ArrowNextIcon, InformativeIcon } from '@coursera/cds-icons';

import withSingleTracked from 'bundles/common/components/withSingleTracked';
import { enterpriseContractUtilizationType } from 'bundles/enterprise-admin-constants/EnterpriseContractConstants';
import { renderMedDate } from 'bundles/enterprise-admin-coursera-for-campus-basic/utils/utils';
import GoogleSetupContainerBase from 'bundles/enterprise-admin-grow-with-google/components/GoogleSetupContainerBase';
import type { Contract, ContractRow } from 'bundles/enterprise-admin-program-creation/types';
import {
  getAvailableContractOptions,
  isContractDisabled,
} from 'bundles/enterprise-admin-program-creation/utils/contract';
import { getFormattedContractType } from 'bundles/enterprise-admin-users/utils';
import TrackedDiv from 'bundles/page/components/TrackedDiv';
import { TrackedA } from 'bundles/page/components/TrackedLink2';

import _t from 'i18n!nls/enterprise-admin-users';

const TrackedButton = withSingleTracked({ type: 'BUTTON' })<ButtonProps>(Button);

export type Props = {
  help?: string;
  helpIcon?: JSX.Element;
  handleContractIdChange: (contractId: string) => void;
  userRosters: {
    contract?: {
      id: string;
    } | null;
    programInvitations?: { paging: { total: number | null } } | null;
  }[];
  orgContracts?: Contract[];
  showCreatedContractsToSuperUsers?: boolean;
  selectedContractId?: string;
  onContinue: () => void;
  onBack?: () => void;
  currentStepIdx?: number;
  footer?: React.ReactNode;
  isGwGScreen?: boolean;
  orgSlug?: string;
  containerStyle?: SerializedStyles;
  skipForward?: boolean;
  skipBackwards?: boolean;
  isInviting?: boolean;
};

const chooseContractContainer = (theme: Theme) =>
  css({
    paddingLeft: '15%',
    paddingRight: '15%',
    '& .cui-tableWrapper thead span': {
      marginTop: 'var(--cds-spacing-300)',
    },
  });

const rcAddNewContractBanner = (theme: Theme) =>
  css({
    background: 'var(--cds-color-grey-25)',
    padding: 'var(--cds-spacing-100) var(--cds-spacing-200)',
    textAlign: 'start',
    marginTop: 'var(--cds-spacing-600)',
    display: 'flex',
    alignItems: 'center',
  });

const contractText = (theme: Theme) =>
  css({
    display: 'inline-block',
    fontSize: '14px',
    marginLeft: 'var(--cds-spacing-150)',
  });

const header = (theme: Theme) =>
  css({
    marginTop: 'var(--cds-spacing-400)',
    marginBottom: 'var(--cds-spacing-100)',
    '&&': {
      fontSize: '24px',
      lineHeight: '30px',
    },
  });

const gwgheader = (theme: Theme) =>
  css({
    fontSize: '24px',
    fontWeight: 600,
    marginBottom: 'var(--cds-spacing-100)',
  });

const description = (theme: Theme) =>
  css({
    '&&': {
      fontStyle: 'normal',
      fontWeight: 'normal',
      fontSize: '16px',
      lineHeight: '30px',
    },
    marginBottom: 'var(--cds-spacing-400)',
  });

const licenseFull = css({
  fontWeight: 'bold',
  textTransform: 'uppercase',
});

const buttonStyles = (theme: Theme) =>
  css({
    padding: '0.5rem 2.5rem',
    marginTop: 'var(--cds-spacing-400)',
  });

const buttonGroup = (theme: Theme) =>
  css({
    marginTop: 'var(--cds-spacing-400)',
  });

const ChooseContractContainer = ({
  help,
  helpIcon,
  onContinue,
  onBack,
  selectedContractId,
  currentStepIdx,
  handleContractIdChange,
  userRosters,
  orgContracts = [],
  showCreatedContractsToSuperUsers = false,
  footer,
  isGwGScreen,
  orgSlug,
  containerStyle,
  skipForward = true,
  skipBackwards = false,
  isInviting = false,
}: Props) => {
  const [query, setQuery] = React.useState('');
  const allContractOptions = orgContracts;

  React.useEffect(() => {
    const availableContractOptions = getAvailableContractOptions(allContractOptions, {
      showCreatedContractsToSuperUsers,
    });
    const firstOption = availableContractOptions[0];
    const onlyOneOptionAvailable = availableContractOptions.length === 1;
    if (firstOption && onlyOneOptionAvailable) {
      const id = firstOption.id;
      if (id) handleContractIdChange(id);

      if (skipForward) {
        onContinue();
      }

      if (skipBackwards && onBack) {
        onBack();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onRosterChange = (contractId: string) => {
    const contract = allContractOptions.find((c) => c?.id === contractId);
    const isDisabled = contract && isContractDisabled(contract, { showCreatedContractsToSuperUsers });
    if (!isDisabled) {
      handleContractIdChange(contractId);
    }
  };

  const onCheckboxClick = (contractId: string) => {
    onRosterChange(contractId);
  };

  const filteredRows = React.useMemo(() => {
    const getNumberOfPendingInvitations = (contractId: string) => {
      return userRosters.reduce((sum, roster) => {
        if (roster.contract?.id === contractId) {
          return sum + (roster.programInvitations?.paging.total ?? 0);
        }
        return sum;
      }, 0);
    };
    const sortedRows = sortBy(allContractOptions, (contract) => contract?.name || contract?.id).filter((contract) =>
      contract.name ? contract.name.toLowerCase().includes(query.trim().toLowerCase()) : true
    );

    return sortedRows.map((contract) => {
      const utilization = contract?.utilization;
      const remainingEnrollment = utilization?.remainingEnrollment;
      const remainingUserLicense = utilization?.remainingUserLicense;
      const totalEnrollment = utilization?.totalEnrollment;
      const totalUserLicense = utilization?.totalUserLicense;
      const isFull = remainingEnrollment === 0 || remainingUserLicense === 0;
      const isDisabled = isContractDisabled(contract, { showCreatedContractsToSuperUsers });
      const isSelected = contract?.id === selectedContractId;
      const type = getFormattedContractType(contract?.limitType);
      let licensesLeft: React.ReactElement;
      if (contract?.limitType === enterpriseContractUtilizationType.USER_LICENSE_WITH_ENROLLMENT_CAP) {
        licensesLeft = (
          <React.Fragment>
            <FormattedMessage
              message={_t(`{remain} of {total} licenses;`)}
              remain={<strong>{remainingUserLicense}</strong>}
              total={totalUserLicense}
            />
            <br />
            <FormattedMessage
              message={_t(`{remain} of {total} enrollments`)}
              remain={<strong>{remainingEnrollment}</strong>}
              total={totalEnrollment}
            />
          </React.Fragment>
        );
      } else if (isFull) {
        licensesLeft = <span css={licenseFull}>{_t('Full')}</span>;
      } else if (contract?.limitType === enterpriseContractUtilizationType.NO_LIMIT) {
        licensesLeft = <strong>{_t('Unlimited')}</strong>;
      } else if (contract?.limitType === enterpriseContractUtilizationType.USER_LICENSE) {
        licensesLeft = (
          <FormattedMessage
            message={_t(`{remain} of {total}`)}
            remain={<strong>{remainingUserLicense}</strong>}
            total={totalUserLicense}
          />
        );
      } else if (contract?.limitType === enterpriseContractUtilizationType.ENROLLMENT_CAP) {
        licensesLeft = (
          <FormattedMessage
            message={_t(`{remain} of {total}`)}
            remain={<strong>{remainingEnrollment}</strong>}
            total={totalEnrollment}
          />
        );
      } else {
        licensesLeft = (
          <FormattedMessage
            message={_t(`{remain} of {total}`)}
            remain={<strong>{utilization?.remainingUserLicense}</strong>}
            total={utilization?.totalUserLicense}
          />
        );
      }

      return {
        key: contract?.id,
        name: contract?.name ?? contract?.id,
        limitType: type,
        remaining: licensesLeft,
        pendingInvitations: getNumberOfPendingInvitations(String(contract?.id)),
        startsAt: contract?.startsAt,
        endsAt: contract?.endsAt,
        isSelected,
        isDisabled,
      };
    });
  }, [query, allContractOptions, userRosters, selectedContractId, showCreatedContractsToSuperUsers]);

  const theme = useTheme();

  const getSupportText = () => {
    const availableContractOptions = getAvailableContractOptions(allContractOptions, {
      showCreatedContractsToSuperUsers,
    });
    if (availableContractOptions.length === 0) {
      return _t('Your program does not have any contracts available. Please contact your customer representative.');
    } else if (isInviting) {
      return _t(
        'Your program has multiple contracts. Which of the following contracts would you like to invite learners to?'
      );
    } else {
      return allContractOptions.length > 0
        ? _t(
            'Your organization has multiple contracts available. Which of the following contracts would you like to link this learning program to.'
          )
        : _t(
            'There are no contracts available to create programs for your organization. Please contact your customer representative.'
          );
    }
  };

  const onCancelClick = () => redirect.setLocation(`/o/${orgSlug}/admin/home`);

  const columns: Array<TableColumn<ContractRow, keyof ContractRow>> = [
    {
      accessor: 'isSelected',
      label: '',
      renderCell: (row) => (
        <RadioInput
          id={`radio-${row.key}`}
          name={`radio-${row.key}`}
          aria-label={`radio-${row.key}`}
          value={row.key}
          checked={row.isSelected}
          disabled={row.isDisabled}
          onChange={(e) => {
            if (!row.isDisabled) {
              onCheckboxClick(e.target.value);
            }
          }}
          data-testid={`radio-${row.key}`}
        />
      ),
    },
    {
      accessor: 'name',
      label: _t('Name'),
      renderCell: (row) => (
        <Typography2
          component="p"
          onClick={() => {
            if (!row.isDisabled) {
              onCheckboxClick(row.key);
            }
          }}
        >
          {row.name}
        </Typography2>
      ),
    },
    {
      accessor: 'limitType',
      label: _t('Type'),
      renderCell: (row) => (
        <Typography2
          component="p"
          onClick={() => {
            if (!row.isDisabled) {
              onCheckboxClick(row.key);
            }
          }}
        >
          {row.limitType}
        </Typography2>
      ),
    },
    {
      accessor: 'remaining',
      label: _t('Remaining'),
      renderCell: (row) => (
        <Typography2
          component="p"
          onClick={() => {
            if (!row.isDisabled) {
              onCheckboxClick(row.key);
            }
          }}
        >
          {row.remaining}
        </Typography2>
      ),
    },
    {
      accessor: 'pendingInvitations',
      label: _t('Pending Invitations'),
      renderCell: (row) => (
        <Typography2
          component="p"
          onClick={() => {
            if (!row.isDisabled) {
              onCheckboxClick(row.key);
            }
          }}
        >
          {row.pendingInvitations}
        </Typography2>
      ),
    },
    {
      accessor: 'startsAt',
      label: _t('Start Date'),
      renderCell: (row) => (
        <Typography2
          component="p"
          onClick={() => {
            if (!row.isDisabled) {
              onCheckboxClick(row.key);
            }
          }}
        >
          {row.startsAt != null ? renderMedDate(row.startsAt) : undefined}
        </Typography2>
      ),
    },
    {
      accessor: 'endsAt',
      label: _t('End Date'),
      renderCell: (row) => (
        <Typography2
          component="p"
          onClick={() => {
            if (!row.isDisabled) {
              onCheckboxClick(row.key);
            }
          }}
        >
          {row.endsAt != null ? renderMedDate(row.endsAt) : undefined}
        </Typography2>
      ),
    },
  ];

  const body = (
    <div
      css={css`
        td:nth-of-type(1),
        th:nth-of-type(1) {
          min-width: 36px;
          max-width: 36px;
          width: 36px;
        }
      `}
    >
      <Typography variant="h2" css={description(theme)} color="supportText">
        {getSupportText()}
      </Typography>
      <Search placeholder={_t('Search for a contract by name...')} fullWidth value={query} onChange={setQuery} />
      <Table
        columns={columns}
        rows={filteredRows}
        totalRows={filteredRows.length}
        hidePagination
        cellVerticalAlign="top"
        maxHeight="400px"
      />
      <div css={rcAddNewContractBanner(theme)}>
        {helpIcon ?? <InformativeIcon />}
        <Typography2 component="p" css={contractText(theme)} variant="bodyPrimary">
          <FormattedMessage
            message={help ?? _t('Need to add more licenses or a new contract? {learnMore}')}
            learnMore={
              <TrackedA
                href="https://business.coursera.help/hc/articles/360054155914-Coursera-for-Campus-Basic-plan-Admin-Support-FAQs"
                data={{
                  currentStep: currentStepIdx,
                }}
                trackingName="learn_more_link"
                target="_blank"
                rel="noopener noreferrer"
              >
                {_t('Learn more here.')}
              </TrackedA>
            }
          />
        </Typography2>
      </div>
    </div>
  );

  if (isGwGScreen) {
    return (
      <GoogleSetupContainerBase title={_t('Choose a contract')}>
        <Typography variant="h1" css={gwgheader(theme)}>
          {_t('Select the contract you want to use for this learning program')}
        </Typography>
        {body}
        <div css={buttonGroup(theme)}>
          <TrackedButton
            variant="primary"
            icon={<ArrowNextIcon />}
            style={{ marginRight: 'var(--cds-spacing-300)' }}
            onClick={onContinue}
            size="medium"
            disabled={!selectedContractId}
            data-e2e="gwg-choose-contract-screen-continue-button"
            trackingName="gwg_choose_contract_continue"
            trackingData={{ orgSlug }}
          >
            {_t('Continue')}
          </TrackedButton>
          <TrackedButton
            variant="secondary"
            onClick={onCancelClick}
            size="medium"
            trackingName="gwg_choose_contract_cancel"
            trackingData={{ orgSlug }}
          >
            {_t('Cancel')}
          </TrackedButton>
        </div>
      </GoogleSetupContainerBase>
    );
  }

  return (
    <TrackedDiv
      css={containerStyle ?? chooseContractContainer(theme)}
      trackingName="choose_contract"
      withVisibilityTracking={true}
      trackClicks={false}
    >
      <Typography variant="h1" css={header(theme)}>
        {_t('Choose contract')}
      </Typography>
      {body}
      {!footer && (
        <TrackedButton
          {...Button.defaultProps}
          variant="primary"
          data-e2e="continue"
          size="medium"
          css={buttonStyles(theme)}
          onClick={onContinue}
          disabled={!selectedContractId}
          trackingName="choose_contract_continue"
          trackingData={{ orgSlug }}
        >
          {_t('Continue')}
        </TrackedButton>
      )}
      {footer}
    </TrackedDiv>
  );
};

export default ChooseContractContainer;
